import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface RouteLazyErrorBoundaryProps {
  children: ReactNode;
}

type BoundaryStatus = "ok" | "reloading" | "failed";

interface RouteLazyErrorBoundaryState {
  status: BoundaryStatus;
}

// sessionStorage, not localStorage — the guard must reset on a fresh tab/
// session, not persist indefinitely. Otherwise a reload attempt from a
// deploy that's since been fixed could permanently block future reloads in
// any tab that happened to hit it once, long after the underlying chunk
// issue is gone.
const RELOAD_GUARD_KEY = "joules_chunk_reload_attempted";

// Browsers/bundlers phrase a missing dynamically-imported module differently
// (e.g. a stale chunk hash after a deploy deleted the old file — see JAA-857
// spec.md Open Question #1), so match a few known substrings rather than one
// exact string.
function isChunkLoadError(error: Error): boolean {
  return (
    /Failed to fetch dynamically imported module/i.test(error.message) ||
    /error loading dynamically imported module/i.test(error.message) ||
    /Importing a module script failed/i.test(error.message)
  );
}

// Must be a class component — getDerivedStateFromError/componentDidCatch
// have no hook equivalent in React, so this can't follow the function-
// component convention used everywhere else in this codebase.
class RouteLazyErrorBoundary extends Component<
  RouteLazyErrorBoundaryProps,
  RouteLazyErrorBoundaryState
> {
  state: RouteLazyErrorBoundaryState = { status: "ok" };

  // Classification happens here, not in componentDidCatch, because React
  // commits this return value as state *before* componentDidCatch runs —
  // by the time render() needs to decide what to show, the answer already
  // exists. Doing this work in componentDidCatch instead would render the
  // "failed" UI for one frame first (no status to act on yet), which is the
  // exact flash this boundary exists to avoid.
  static getDerivedStateFromError(error: Error): RouteLazyErrorBoundaryState {
    if (!isChunkLoadError(error)) {
      return { status: "failed" };
    }
    // Reload guard lives in sessionStorage (not component state) because a
    // reload discards component state entirely — without a persisted flag
    // a chunk that's genuinely broken (not just stale) would reload forever.
    const alreadyAttempted = sessionStorage.getItem(RELOAD_GUARD_KEY) === "true";
    return { status: alreadyAttempted ? "failed" : "reloading" };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo): void {
    // componentDidCatch fires for every error this boundary catches, not
    // just reloadable ones — this guard is what actually limits the reload
    // side effect to the "reloading" classification getDerivedStateFromError
    // already made; everything else (non-chunk errors, already-attempted
    // chunk errors) falls through to the static fallback UI in render().
    if (this.state.status !== "reloading") return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, "true");
    window.location.reload();
  }

  render(): ReactNode {
    if (this.state.status === "ok") {
      return this.props.children;
    }

    if (this.state.status === "reloading") {
      // Render nothing — window.location.reload() is already in flight from
      // componentDidCatch. Showing the fallback UI here would just flash
      // before the page navigates away.
      return null;
    }

    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <p className="text-[14px] font-medium text-[#181D27]">
          Something went wrong loading this page.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-[var(--deep-sky-denim)] px-4 py-2 text-[13px] font-medium text-white transition-opacity hover:opacity-90 cursor-pointer"
        >
          Reload
        </button>
      </div>
    );
  }
}

export default RouteLazyErrorBoundary;
